import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  IconButton,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  Close as CloseIcon,
  Send as SendIcon,
  Phone as PhoneIcon,
  Person as PersonIcon,
  Message as MessageIcon,
} from '@mui/icons-material';

const NewConversationModal = ({ open, onClose, onStartConversation }) => {
  const [phoneNumber, setPhoneNumber] = useState('');
  const [contactName, setContactName] = useState('');
  const [initialMessage, setInitialMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async () => {
    if (!phoneNumber.trim()) {
      setError('Phone number is required');
      return;
    }

    // Basic phone validation
    const cleanPhone = phoneNumber.replace(/\s/g, '');
    if (!/^(\+?\d{10,15})$/.test(cleanPhone)) {
      setError('Please enter a valid phone number (10-15 digits)');
      return;
    }

    setLoading(true);
    setError('');

    try {
      await onStartConversation(phoneNumber, contactName, initialMessage);
      // Reset form
      setPhoneNumber('');
      setContactName('');
      setInitialMessage('');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to start conversation');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setPhoneNumber('');
      setContactName('');
      setInitialMessage('');
      setError('');
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">Start New Conversation</Typography>
          <IconButton onClick={handleClose} size="small" disabled={loading}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            label="Phone Number"
            placeholder="+44 7803 165568"
            value={phoneNumber}
            onChange={(e) => setPhoneNumber(e.target.value)}
            fullWidth
            required
            disabled={loading}
            InputProps={{
              startAdornment: <PhoneIcon sx={{ mr: 1, color: 'text.secondary' }} />,
            }}
            helperText="Enter phone number with country code (e.g., +44 for UK)"
          />

          <TextField
            label="Contact Name (Optional)"
            placeholder="John Smith"
            value={contactName}
            onChange={(e) => setContactName(e.target.value)}
            fullWidth
            disabled={loading}
            InputProps={{
              startAdornment: <PersonIcon sx={{ mr: 1, color: 'text.secondary' }} />,
            }}
          />

          <TextField
            label="Initial Message (Optional)"
            placeholder="Hi! This is a message from Autohub Autocentres..."
            value={initialMessage}
            onChange={(e) => setInitialMessage(e.target.value)}
            fullWidth
            multiline
            rows={3}
            disabled={loading}
            InputProps={{
              startAdornment: <MessageIcon sx={{ mr: 1, mt: 1, color: 'text.secondary' }} />,
            }}
            helperText="Leave empty to create conversation without sending a message"
          />
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={loading || !phoneNumber.trim()}
          startIcon={loading ? <CircularProgress size={20} /> : <SendIcon />}
          color="primary"
        >
          {loading ? 'Starting...' : initialMessage ? 'Start & Send' : 'Start Conversation'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default NewConversationModal;
